// Image URL safety + the single CDN-transform entry point for apps/web.
//
// Per the imagery addendum §I6.2, every display size comes from a Cloudflare
// image transform produced by ONE helper — `buildTransformedUrl` — so slot
// sizing and the house `dpr=2` rule live in exactly one place. §I6.3 fixes the
// slot dimensions; the presets at the bottom of this file are the sanctioned
// set (the lightbox row is the PROPOSED §I6.3 addition pending Ryan sign-off).

/**
 * ADV-1254: media/attachment URLs are tenant-supplied and rows predating the
 * schema hardening may still carry an executable scheme. Only treat a URL as
 * loadable/clickable when it is an absolute http(s) URL — a `javascript:` or
 * `data:` href would otherwise run script in the viewer's session. Extracted
 * from attachment-strip.tsx so every imagery surface shares one guard.
 */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:"
    ? url
    : null;
}

export interface TransformParams {
  /** Rendered DISPLAY width in CSS px (NOT pre-doubled — `dpr` handles retina). */
  width: number;
  /** Rendered DISPLAY height in CSS px. Omit for width-only (scale-down) slots. */
  height?: number;
  /** `cover` crops to the slot; `scale-down` never upscales and never crops. */
  fit?: "cover" | "scale-down" | "contain";
  /** Device-pixel-ratio multiplier. House rule: 2 (retina) unless a raw view. */
  dpr?: number;
  /**
   * Crop focal point. `face` (ADV-1354) detects faces so a full-body headshot
   * crops to the face in an avatar slot, not the torso; `auto` uses saliency
   * (§I6.3 banners). Omit to let `fit=cover` center-crop. Only meaningful with
   * `fit=cover`.
   */
  gravity?: "auto" | "face" | "left" | "right" | "top" | "bottom";
}

// Hosts where Cloudflare image resizing is enabled, so a `public_url` on this
// host can be rewritten to the `/cdn-cgi/image/<opts>/<path>` form. Confirmed
// live on the demo CDN (the mockup chrome uses it: chrome.js `cdnImage`).
// Any OTHER host (tenant uploads, the dead `*.r2.dev` fallback per ADV-1332)
// passes through UNtransformed per §I6.3 — the slot's fallback chain (§I7)
// covers a URL that then fails to load.
const TRANSFORM_ENABLED_HOSTS = new Set<string>(["demo-assets.getadva.ai"]);

/**
 * The §I6.2-mandated single CDN-transform entry point. Given a stored
 * `public_url` (a full absolute URL) and a slot's display params, returns the
 * Cloudflare-transformed URL — `https://<host>/cdn-cgi/image/<opts>/<path>` —
 * for transform-enabled hosts, or the safe original URL untransformed for any
 * other host. Returns `null` only when the input is absent or not http(s), so
 * callers can branch straight to the §I7 fallback chain.
 */
export function buildTransformedUrl(
  publicUrl: string | null | undefined,
  params: TransformParams,
): string | null {
  const safe = safeHttpUrl(publicUrl);
  if (!safe) return null;

  let url: URL;
  try {
    url = new URL(safe);
  } catch {
    return null;
  }

  // Already a transform URL → never double-wrap.
  if (url.pathname.includes("/cdn-cgi/image/")) return safe;

  // Unknown / tenant host → pass through untransformed (§I6.3).
  if (!TRANSFORM_ENABLED_HOSTS.has(url.hostname)) return safe;

  const opts = [`width=${params.width}`];
  if (params.height != null) opts.push(`height=${params.height}`);
  opts.push(`fit=${params.fit ?? "cover"}`);
  opts.push("format=auto");
  opts.push(`dpr=${params.dpr ?? 2}`);
  if (params.gravity) opts.push(`gravity=${params.gravity}`);

  const key = url.pathname.replace(/^\//, "");
  return `${url.origin}/cdn-cgi/image/${opts.join(",")}/${key}${url.search}`;
}

// ── §I6.3 slot presets (display sizes; `dpr=2` applied inside the helper) ──

/** Gallery grid tile — §I6.3 card-hero slot, cropped square-ish evidence. */
export const GALLERY_TILE: TransformParams = {
  width: 240,
  height: 160,
  fit: "cover",
};

/**
 * Lightbox image — PROPOSED §I6.3 row (pending Ryan sign-off). `scale-down`
 * never upscales the ~1K per-job sources and never crops the evidence the
 * operator opened the lightbox to inspect.
 */
export const LIGHTBOX_IMAGE: TransformParams = {
  width: 1024,
  fit: "scale-down",
};

/** Overview preview-strip thumb — §I6.3 row-inline thumb (used by the Overview strip, post-tracer-bullet). */
export const OVERVIEW_THUMB: TransformParams = {
  width: 120,
  height: 80,
  fit: "cover",
};
